import { clearLastPresentVisitDateKey } from "utils/activeDatesEducationTriggers";
import {
  isLegacyScheduleJuly2026ConversionComplete,
  isLegacyScheduleJuly2026ConversionNotApplicable,
  isLegacyScheduleJuly2026ConversionWizardEligible,
} from "utils/legacyScheduleJuly2026Conversion";

export const ACTIVE_DATES_EDUCATION_PREVIEW_STORAGE_KEY = "llp-active-dates-education-preview";
export const ACTIVE_DATES_EMPTY_PRESENT_STEPS = Object.freeze({
  INTRO: "intro",
  UNDERSTANDING: "understanding",
  CHOICES: "choices",
});
export const ACTIVE_DATES_UNDERSTANDING_LEVELS = Object.freeze({
  UNDERSTANDS: "understands",
  STARTING: "starting",
  CONFUSED: "confused",
});
const EMPTY_PRESENT_SESSION_HIDDEN_KEY_PREFIX = "activeDatesEducationEmptyPresentSessionHidden:";

const hasActiveDatesEducationProgress = (user = {}) =>
  Object.values(getActiveDatesEducationState(user)).some(Boolean);

const isLikelyNewUserForActiveDatesEducation = (user = {}) => {
  const productTourStatus = String(user?.productTour?.status || "").toLowerCase();
  return Boolean(
    user?.onboardingIncomplete ||
    productTourStatus === "new" ||
    productTourStatus === "in_progress"
  );
};

export const shouldSuppressActiveDatesEducationForLegacyScheduleConversion = (user = {}) =>
  isLegacyScheduleJuly2026ConversionWizardEligible(user) ||
  (
    !hasActiveDatesEducationProgress(user) &&
    !isLikelyNewUserForActiveDatesEducation(user) &&
    (
      isLegacyScheduleJuly2026ConversionComplete(user?.legacyScheduleJuly2026Conversion) ||
      isLegacyScheduleJuly2026ConversionNotApplicable(user?.legacyScheduleJuly2026Conversion)
    )
  );

const buildKey = (prefix, uid) => (uid ? `${prefix}${uid}` : null);

const getStorageItem = (storage, key) => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const setStorageItem = (storage, key, value) => {
  try {
    storage?.setItem(key, value);
  } catch {
    // no-op
  }
};

export function isActiveDatesEducationPreviewEnabled() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(ACTIVE_DATES_EDUCATION_PREVIEW_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function enableActiveDatesEducationPreview() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    window.localStorage.setItem(ACTIVE_DATES_EDUCATION_PREVIEW_STORAGE_KEY, "1");
    console.info(
      "[Active Dates education preview] Enabled. Reload Present to replay orientation and confirm flows. "
      + "Disable with window.__lessonLaunchpadActiveDatesEducationPreview.disable()"
    );
    return true;
  } catch {
    return false;
  }
}

export function disableActiveDatesEducationPreview() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    window.localStorage.removeItem(ACTIVE_DATES_EDUCATION_PREVIEW_STORAGE_KEY);
    console.info("[Active Dates education preview] Disabled.");
    return true;
  } catch {
    return false;
  }
}

export function syncActiveDatesEducationPreviewFromUrl() {
  if (typeof window === "undefined") {
    return isActiveDatesEducationPreviewEnabled();
  }

  const params = new URLSearchParams(window.location.search);
  const previewParam = params.get("llpActiveDatesEducationPreview");

  if (previewParam === "1") {
    enableActiveDatesEducationPreview();
  } else if (previewParam === "0") {
    disableActiveDatesEducationPreview();
  }

  return isActiveDatesEducationPreviewEnabled();
}

export function clearEmptyPresentEducationSessionDismiss(uid) {
  if (!uid || typeof window === "undefined") {
    return;
  }

  const key = buildKey(EMPTY_PRESENT_SESSION_HIDDEN_KEY_PREFIX, uid);
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // no-op
  }
}

export function getActiveDatesEducationPreviewUser(user = {}) {
  if (!isActiveDatesEducationPreviewEnabled()) {
    return user;
  }

  return {
    ...user,
    activeDatesEducation: {},
  };
}

export function shouldPersistActiveDatesEducationDismiss() {
  return !isActiveDatesEducationPreviewEnabled();
}

export function getActiveDatesEducationState(user = {}) {
  return user?.activeDatesEducation || {};
}

export function isEmptyPresentEducationDismissed(user = {}) {
  return Boolean(getActiveDatesEducationState(user).emptyPresentDismissedAt);
}

export function isEmptyPresentEducationCompleted(user = {}) {
  return Boolean(getActiveDatesEducationState(user).emptyPresentCompletedAt);
}

export function isMultiDayConfirmDismissed(user = {}) {
  if (isActiveDatesEducationPreviewEnabled()) {
    return false;
  }

  return Boolean(getActiveDatesEducationState(user).multiDayConfirmDismissedAt);
}

export function isClassNotesNonSchoolDateWarningDismissed(user = {}) {
  if (isActiveDatesEducationPreviewEnabled()) {
    return false;
  }

  return Boolean(getActiveDatesEducationState(user).classNotesNonSchoolDateWarningDismissedAt);
}

export function hideEmptyPresentEducationForSession(uid) {
  if (isActiveDatesEducationPreviewEnabled()) {
    return;
  }

  if (!uid || typeof window === "undefined") {
    return;
  }

  const key = buildKey(EMPTY_PRESENT_SESSION_HIDDEN_KEY_PREFIX, uid);
  setStorageItem(window.sessionStorage, key, "true");
}

export function isEmptyPresentEducationHiddenThisSession(uid) {
  if (isActiveDatesEducationPreviewEnabled()) {
    return false;
  }

  if (!uid || typeof window === "undefined") {
    return false;
  }

  const key = buildKey(EMPTY_PRESENT_SESSION_HIDDEN_KEY_PREFIX, uid);
  return getStorageItem(window.sessionStorage, key) === "true";
}

export function shouldShowEmptyPresentEducation(user = {}) {
  if (isActiveDatesEducationPreviewEnabled()) {
    return true;
  }

  const uid = user?.uid;
  return (
    isLikelyNewUserForActiveDatesEducation(user)
    && !isEmptyPresentEducationDismissed(user)
    && !isEmptyPresentEducationCompleted(user)
    && !isEmptyPresentEducationHiddenThisSession(uid)
    && !shouldSuppressActiveDatesEducationForLegacyScheduleConversion(user)
  );
}

export function resolveEmptyPresentEducationInitialStep(user = {}) {
  if (isActiveDatesEducationPreviewEnabled()) {
    return ACTIVE_DATES_EMPTY_PRESENT_STEPS.INTRO;
  }

  const state = getActiveDatesEducationState(user);
  if (state.emptyPresentUnderstandingLevel) {
    return ACTIVE_DATES_EMPTY_PRESENT_STEPS.CHOICES;
  }

  if (state.emptyPresentVideoProceedAt) {
    return ACTIVE_DATES_EMPTY_PRESENT_STEPS.UNDERSTANDING;
  }

  return ACTIVE_DATES_EMPTY_PRESENT_STEPS.INTRO;
}

export function buildActiveDatesEducationPatch(user = {}, fields = {}) {
  return {
    activeDatesEducation: {
      ...getActiveDatesEducationState(user),
      ...fields,
    },
  };
}

export function buildEmptyPresentDismissPatch(user = {}, dismissedAt = new Date()) {
  return buildActiveDatesEducationPatch(user, {
    emptyPresentDismissedAt: dismissedAt,
  });
}

export function buildEmptyPresentVideoProceedPatch(user = {}, proceededAt = new Date()) {
  return buildActiveDatesEducationPatch(user, {
    emptyPresentVideoProceedAt: proceededAt,
  });
}

export function buildEmptyPresentUnderstandingPatch(user = {}, understandingLevel, understoodAt = new Date()) {
  return buildActiveDatesEducationPatch(user, {
    emptyPresentUnderstandingLevel: understandingLevel,
    emptyPresentUnderstandingAt: understoodAt,
  });
}

export function buildEmptyPresentCompletedPatch(user = {}, completedAt = new Date()) {
  return buildActiveDatesEducationPatch(user, {
    emptyPresentCompletedAt: completedAt,
  });
}

export function buildMultiDayConfirmDismissPatch(user = {}, dismissedAt = new Date()) {
  return buildActiveDatesEducationPatch(user, {
    multiDayConfirmDismissedAt: dismissedAt,
  });
}

export function buildClassNotesNonSchoolDateWarningDismissPatch(user = {}, dismissedAt = new Date()) {
  return buildActiveDatesEducationPatch(user, {
    classNotesNonSchoolDateWarningDismissedAt: dismissedAt,
  });
}

if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  window.__lessonLaunchpadActiveDatesEducationPreview = {
    enable: enableActiveDatesEducationPreview,
    disable: disableActiveDatesEducationPreview,
    isEnabled: isActiveDatesEducationPreviewEnabled,
    clearSessionDismiss: clearEmptyPresentEducationSessionDismiss,
    clearLastPresentVisit: clearLastPresentVisitDateKey,
  };
}
